import Select, { type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { SelectColorPicker } from '@databases/color-picker';
import { messages } from '@databases/intl/messages';
import SelectOptionTag from '@databases/tag/SelectOptionTag';
import {
  DatabaseFieldType,
  type SelectOption,
  type DatabaseField,
  type RawDatabaseValue
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type MultiChange,
  MultiValueChangeType,
  type DatabaseChange,
  type ValueChange
} from '@databases/utils/database/changesets';
import { pickNewColor } from '@databases/utils/database/field-types/select';
import { generateId } from '@databases/utils/utils';
import React, { type FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling
} from '../popover-edit-common';

const getOptionValue = (op: SelectOption) => op.id ?? op.label;
const getOptionLabel = (op: SelectOption) => op.label;
function hasOption(options: SelectOption[], option: SelectOption): boolean {
  return options.some((op) => (op.id ?? op.label) === (option.id ?? option.label));
}

const CreateOptionContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
`;

export interface TagSelectProps {
  options: SelectOption[];
  value?: string[];
  isCreatable?: boolean;
  isMulti: boolean;
  isInvalid?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  preventOpenWhenNoOptions?: boolean;
  appearance?: 'default' | 'none' | 'subtle' | undefined;
  field: DatabaseField<DatabaseFieldType.SELECT>;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  addChangeToDraft?: (change: DatabaseChange) => void;
  onChange: (
    value: RawDatabaseValue<DatabaseFieldType.SELECT>,
    change: ValueChange | null
  ) => void;
  isRenderedInSidePanel?: boolean;
  addDataSectionRef: React.MutableRefObject<HTMLDivElement | null>;
  initialKey?: string;
}

const TagSelect: FC<TagSelectProps> = ({
  options,
  value = Object.freeze([]) as never[],
  isCreatable = true,
  isMulti,
  isInvalid = false,
  autoFocus,
  placeholder,
  appearance,
  field,
  onFocus,
  onBlur,
  addChangeToDraft,
  onChange,
  isRenderedInSidePanel,
  addDataSectionRef,
  initialKey
}) => {
  const [nextColor, setNextColor] = useState(pickNewColor(options));
  useEffect(() => {
    setNextColor(pickNewColor(options));
  }, [options]);

  const [inputValue, setInputValue] = useState(initialKey ?? '');
  const handleInputChange = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  const { formatMessage } = useIntl();

  const resolvedOptions = useMemo(() => {
    const result = [...options];
    if (isCreatable) {
      const trimmed = inputValue.trim();
      const trimmedLower = inputValue.trim().toLowerCase();
      if (trimmed && !options?.some((o) => o.label.toLowerCase() === trimmedLower)) {
        result.unshift({ id: generateId(), label: trimmed, color: nextColor });
      }
    }
    return result.filter((o) => o.label.trim().length > 0);
  }, [inputValue, isCreatable, nextColor, options]);

  const resolvedValue = useMemo(
    () => options.filter((op) => value.includes(op.id ?? op.label)),
    [options, value]
  );

  const handleCreate = useCallback(
    (option: SelectOption) => {
      const optionValue = option.id ?? option.label;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      addChangeToDraft!({
        type: DatabaseChangeType.SET_SELECT_OPTIONS,
        fieldId: field.id,
        newValue: [...field.settings.selectOptions, option],
        change: {
          type: MultiValueChangeType.APPEND,
          value: option
        }
      });

      const tagValues = resolvedValue.map((v) => v.id ?? v.label);

      onChange([...tagValues, optionValue], {
        type: DatabaseFieldType.SELECT,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: optionValue
        }
      });
    },
    [addChangeToDraft, field.id, field.settings.selectOptions, onChange, resolvedValue]
  );

  const handleSelect = useCallback(
    (passedValue: string | null) => {
      if (!passedValue) {
        return;
      }
      const tagValues = resolvedValue.map((v) => v.id ?? v.label);

      const delta: MultiChange<DatabaseFieldType.SELECT> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: passedValue
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [passedValue]
          };

      onChange([...tagValues, passedValue], {
        type: DatabaseFieldType.SELECT,
        delta
      });
    },
    [isMulti, onChange, resolvedValue]
  );

  const handleChange = useCallback(
    (selectedValue: SelectOption | null) => {
      if (selectedValue) {
        if (!hasOption(options, selectedValue)) {
          handleCreate(selectedValue);
        }
        handleSelect(selectedValue.id ?? selectedValue.label);
      }
    },
    [handleSelect, handleCreate, options]
  );

  const formatOptionLabel = useCallback(
    (option: SelectOption, meta: FormatOptionLabelMeta<SelectOption>) => {
      if (meta.context === 'menu' && !hasOption(options, option)) {
        return (
          <CreateOptionContainer>
            {formatMessage(messages['field-settings.select-picker-create-text'])}
            {
              // Intentionally adding whitespace
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              ' '
            }
            <SelectOptionTag option={option} />
            <SelectColorPicker
              value={option.color}
              onChange={(color) => {
                setNextColor(color);
              }}
              stopPropagation
              size={24}
            />
          </CreateOptionContainer>
        );
      } else if (meta.context === 'menu' || !isMulti) {
        return <SelectOptionTag option={option} />;
      } else {
        return option.label;
      }
    },
    [formatMessage, isMulti, options]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (!isRenderedInSidePanel) {
        addDataSectionRef.current?.getElementsByTagName('input')[0].focus();
      }
    },
    [onBlur, addDataSectionRef, isRenderedInSidePanel]
  );

  return (
    <Select<SelectOption>
      classNamePrefix="select-option-picker"
      options={resolvedOptions}
      value={resolvedValue}
      onChange={handleChange}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      onBlur={handleBlur}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      placeholder={
        placeholder ??
        (isCreatable
          ? formatMessage(messages['select-picker-popover-edit.createable-placeholder'])
          : formatMessage(messages['select-picker-popover-edit.placeholder']))
      }
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      isInvalid={isInvalid}
      onFocus={onFocus}
      autoFocus={autoFocus}
      formatOptionLabel={formatOptionLabel}
      styles={
        isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
      }
      appearance={appearance}
      hideSelectedOptions
      controlShouldRenderValue={false}
      {...(!isRenderedInSidePanel && {
        menuPortalTarget: null,
        defaultMenuIsOpen: true,
        menuIsOpen: true,
        components: {
          IndicatorsContainer: () => null,
          ValueContainer: CustomValueContainer
        }
      })}
    />
  );
};

export default TagSelect;
